import { QueryParams, TAG_TYPES } from '../types';
import { baseApi } from './index';
import { BillResponse, EInvoiceResponse, SaleOrder, SaleOrderPayload, SaleOrderResponse } from 'models/SaleOrder';
import { getQueryParams } from 'utils';

const urlEndpoint = `${process.env.REACT_APP_BASE_URL}/sale-orders`;
const tagType = TAG_TYPES.SaleOrder;

const saleOrderApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllSaleOrders: builder.query<
      {
        data: {
          rows: SaleOrder[];
          count: number;
          kpis: {
            paid: number;
            debt: number;
            cancel: number;
          };
        };
        success: boolean;
      },
      { query: QueryParams }
    >({
      query: (params) => {
        const query = getQueryParams(params?.query);
        return {
          url: `${urlEndpoint}?${query}`,
          method: 'GET',
        };
      },
      providesTags: (result) => {
        return result
          ? [...(result?.data.rows || []).map(({ id }) => ({ type: tagType, id })), { type: tagType, id: 'LIST' }]
          : [{ type: tagType, id: 'LIST' }];
      },
    }),

    getSaleOrderById: builder.query<
      {
        data: SaleOrder;
        success: boolean;
      },
      { id: string; query?: QueryParams }
    >({
      query: (params) => {
        const queryString = params.query ? `?${getQueryParams(params.query)}` : '';
        return {
          url: `${urlEndpoint}/${params.id}${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, params) => [{ type: tagType, id: params.id }],
    }),

    createSaleOrder: builder.mutation<SaleOrderResponse, SaleOrderPayload>({
      query: (data) => ({
        url: urlEndpoint,
        method: 'POST',
        data: data,
      }),
      invalidatesTags: [
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),

    updateSaleOrder: builder.mutation<SaleOrderResponse, Partial<SaleOrderPayload> & { id: string }>({
      query: ({ id, ...patch }) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'PUT',
        data: patch,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
        // { type: TAG_TYPES.Debt, id: 'LIST' }
      ],
    }),
    updateDraftSaleOrder: builder.mutation<SaleOrderResponse, { id: string; data: SaleOrderPayload }>({
      query: ({ id, data }) => ({
        url: `/sale-orders/${id}/draft`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),
    deleteSaleOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `${urlEndpoint}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: tagType, id },
        { type: tagType, id: 'LIST' },
      ],
    }),

    // Check if e-invoice exists for a sale order
    getSaleOrderInvoiceExistence: builder.query<
      {
        data: {
          exists: boolean;
          invoice: {
            id: string;
            status: string;
          };
        };
        success: boolean;
      },
      string
    >({
      query: (id) => ({
        url: `${urlEndpoint}/${id}/e-invoices/existence`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: tagType, id }],
    }),

    createSaleOrderBill: builder.mutation<BillResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${urlEndpoint}/${id}/bills`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),

    createSaleOrderInvoice: builder.mutation<EInvoiceResponse, { id: string }>({
      query: ({ id }) => ({
        url: `${urlEndpoint}/${id}/e-invoices`,
        method: 'POST',
      }),
      invalidatesTags: [
        { type: tagType, id: 'LIST' },
        { type: TAG_TYPES.Debt, id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetAllSaleOrdersQuery,
  useGetSaleOrderByIdQuery,
  useLazyGetSaleOrderByIdQuery,
  useCreateSaleOrderMutation,
  useUpdateSaleOrderMutation,
  useUpdateDraftSaleOrderMutation,
  useDeleteSaleOrderMutation,
  useGetSaleOrderInvoiceExistenceQuery,
  useCreateSaleOrderBillMutation,
  useCreateSaleOrderInvoiceMutation,
} = saleOrderApiService;
